import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

/** Site record from the API — mirrors the `sites` D1 table. Server uses
 *  SELECT * so every column lands on the client. Keep this in sync with
 *  drizzle migrations on the table, or TypeScript's drift-catching doesn't
 *  catch drift (the bar's "#1 bug source"). */
export interface Site {
  id: string
  domain: string
  url: string
  tier: string // 'free' | 'managed'
  schedule_frequency: string | null
  schedule_enabled: number
  schedule_next_run_at: string | null
  schedule_last_run_at: string | null
  schedule_config: string | null
  alert_email: string | null
  latest_crawl_id: string | null
  latest_crawl_status: string | null
  latest_crawl_at: string | null
  latest_avg_seo_score: number | null
  latest_pages_crawled: number | null
  latest_issues_count: number | null
  rank_check_frequency: string | null
  rank_check_next_at: string | null
  default_keyword_location: string | null
  default_keyword_language: string | null
  default_keyword_engine: string | null
  is_wordpress: number
  wordpress_version: string | null
  wordpress_seo_plugin: string | null
  estimated_traffic: number | null
  estimated_traffic_at: string | null
  latest_health_score: number | null
  brand_name: string | null
  brand_logo_url: string | null
  brand_color: string | null
  is_private: number
  gsc_snapshot_next_at?: string | null
  gsc_backfill_done?: number
  bing_snapshot_next_at?: string | null
  ga4_snapshot_next_at?: string | null
  ga4_backfill_done?: number
  created_by: string | null
  creator_name: string | null
  created_at: string
  updated_at: string
}

/** Site crawl history item */
export interface SiteCrawl {
  id: string
  url: string
  domain: string
  status: string
  pages_found: number
  pages_crawled: number
  issues_count: number
  avg_seo_score: number | null
  started_at: string
  completed_at: string | null
  created_at: string
}

/** List all sites */
export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => apiClient.get<{ sites: Site[] }>('/api/sites'),
  })
}

/** Get a single site by ID */
export function useSite(siteId: string) {
  return useQuery({
    queryKey: ['sites', siteId],
    queryFn: async () => {
      const res = await apiClient.get<{ site: Site }>(`/api/sites/${siteId}`)
      return res.site
    },
    enabled: !!siteId,
  })
}

/** Create a new site */
export function useCreateSite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      url: string
      frequency?: string
      maxPages?: number
      rateLimit?: number
      respectRobots?: boolean
      alertEmail?: string
    }) => apiClient.post<{ id: string; domain: string }>('/api/sites', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
    },
  })
}

/** Update site schedule/alert config */
export function useUpdateSite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string
      frequency?: string
      enabled?: boolean
      maxPages?: number
      rateLimit?: number
      respectRobots?: boolean
      checkExternalLinks?: boolean
      collectLighthouse?: boolean
      alertEmail?: string | null
      tier?: 'free' | 'managed'
      rankCheckFrequency?: string
      defaultKeywordLocation?: string
      defaultKeywordLanguage?: string
      defaultKeywordEngine?: string
      isPrivate?: boolean
    }) => apiClient.patch<{ ok: boolean }>(`/api/sites/${id}`, data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      queryClient.invalidateQueries({ queryKey: ['sites', vars.id] })
    },
  })
}

/** Delete a site and all its data */
export function useDeleteSite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ ok: boolean }>(`/api/sites/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
    },
  })
}

/** Get crawl history for a site */
export function useSiteCrawls(siteId: string) {
  return useQuery({
    queryKey: ['sites', siteId, 'crawls'],
    queryFn: () => apiClient.get<{ crawls: SiteCrawl[] }>(`/api/sites/${siteId}/crawls`),
    enabled: !!siteId,
  })
}

/** Start a new crawl for a site */
export function useCreateSiteCrawl(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<{ id: string; status: string }>(`/api/sites/${siteId}/crawls`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'crawls'] })
      queryClient.invalidateQueries({ queryKey: ['sites', siteId] })
      queryClient.invalidateQueries({ queryKey: ['crawls'] })
    },
  })
}

/** Update site branding (for client-facing reports) */
export function useUpdateBranding(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { brandName?: string; brandLogoUrl?: string; brandColor?: string }) =>
      apiClient.put<{ ok: boolean }>(`/api/sites/${siteId}/branding`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites', siteId] })
      queryClient.invalidateQueries({ queryKey: ['sites'] })
    },
  })
}

// ── Portfolio + Alerts ──────────────────────────────────────────

export interface PortfolioStats {
  stats: {
    total_sites: number
    managed_count: number
    free_count: number
    critical_sites: number
    never_crawled: number
    offline_sites: number
    managed_avg_score: number | null
    overall_avg_score: number | null
    avg_health_score: number | null
  }
  worstPerformers: Array<{
    id: string
    domain: string
    url: string
    tier: string
    latest_avg_seo_score: number | null
    latest_issues_count: number | null
    latest_pages_crawled: number | null
    latest_crawl_at: string | null
    schedule_enabled: number
    is_wordpress: number
  }>
  offlineSites: Array<{
    id: string
    domain: string
    url: string
    tier: string
    latest_pages_crawled: number | null
    latest_crawl_at: string | null
    schedule_enabled: number
  }>
  unacknowledgedAlerts: number
}

export interface SiteAlert {
  id: string
  site_id: string
  crawl_id: string
  alert_type: string
  summary: string
  acknowledged: number
  created_at: string
  domain: string
  tier: string
}

/** Portfolio aggregate stats */
export function usePortfolioStats() {
  return useQuery({
    queryKey: ['portfolio'],
    queryFn: () => apiClient.get<PortfolioStats>('/api/sites/portfolio'),
    staleTime: 2 * 60 * 1000, // 2 min
  })
}

/** Site alerts (optionally filtered by acknowledged status).
 *  Returns `alerts` (capped at server LIMIT, default 50) plus `totalCount`
 *  (the true row count matching the filter — for accurate badge display). */
export function useSiteAlerts(acknowledged?: boolean) {
  const params = acknowledged !== undefined ? `?acknowledged=${acknowledged ? '1' : '0'}` : ''
  return useQuery({
    queryKey: ['site-alerts', acknowledged],
    queryFn: () => apiClient.get<{ alerts: SiteAlert[]; totalCount: number }>(`/api/sites/alerts${params}`),
    staleTime: 60 * 1000,
  })
}

/** Acknowledge an alert */
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (alertId: string) =>
      apiClient.post<{ ok: boolean }>(`/api/sites/alerts/${alertId}/acknowledge`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['portfolio'] })
    },
  })
}

/** Snooze or won't-fix an alert */
export function useSnoozeAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ alertId, days }: { alertId: string; days: number }) =>
      apiClient.post<{ ok: boolean }>(`/api/sites/alerts/${alertId}/snooze`, { days }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['portfolio'] })
    },
  })
}

// ── Alert Preferences ────────────────────────────────────────

export interface AlertPreferenceChannel {
  channel: string
  enabled: number
}

export interface AlertPreference {
  type: string
  channels: AlertPreferenceChannel[]
}

/** Fetch alert preference matrix for current user */
export function useAlertPreferences() {
  return useQuery({
    queryKey: ['alert-preferences'],
    queryFn: () => apiClient.get<{ preferences: AlertPreference[] }>('/api/sites/alert-preferences'),
    staleTime: 5 * 60 * 1000,
  })
}

/** Update a single alert preference toggle */
export function useUpdateAlertPreference() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { alertType: string; channel: string; enabled: boolean }) =>
      apiClient.put<{ ok: boolean }>('/api/sites/alert-preferences', params),
    onMutate: async (params) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: ['alert-preferences'] })
      const previous = queryClient.getQueryData<{ preferences: AlertPreference[] }>(['alert-preferences'])

      if (previous) {
        const updated = {
          preferences: previous.preferences.map(pref => ({
            ...pref,
            channels: pref.channels.map(ch =>
              pref.type === params.alertType && ch.channel === params.channel
                ? { ...ch, enabled: params.enabled ? 1 : 0 }
                : ch
            ),
          })),
        }
        queryClient.setQueryData(['alert-preferences'], updated)
      }

      return { previous }
    },
    onError: (_err, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['alert-preferences'], context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-preferences'] })
    },
  })
}
